import { Link, useParams } from "react-router-dom";
import {
  COUNTRY_INFO,
  getCountryInfo,
  type CountryInfo as CountryInfoType,
} from "@client/lib/countryInfo";

/**
 * Länder-Info (Rules & Regulations) für das Web. Spiegelt die mobilen Screens
 * CountryInfoScreen + CountryDetailScreen 1:1 und nutzt dieselbe RN-freie
 * Datenquelle (`@client/lib/countryInfo`), damit es keine Duplikate gibt.
 */
export function CountryInfoList() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed">
        Wähle ein Land, um Rules &amp; Regulations für Drohnenflüge zu sehen:
        EU-Basics, nationale Besonderheiten, Pflichten und offizielle Karten.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {COUNTRY_INFO.map((c) => (
          <Link
            key={c.iso}
            to={`/laender/${c.iso}`}
            data-testid={`country-${c.iso}`}
            className="card flex items-center gap-3 hover:border-brand-300 hover:bg-brand-50 transition"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700 font-extrabold tracking-wide">
              {c.iso}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-[var(--color-app-text)] truncate">
                {c.name}
              </span>
              <span className="block text-xs text-[var(--color-app-text-secondary)]">
                Rules &amp; Regulations
              </span>
            </span>
            <span className="text-[var(--color-app-text-tertiary)]" aria-hidden>
              ›
            </span>
          </Link>
        ))}
      </div>

      <p className="text-[11px] italic text-[var(--color-app-text-tertiary)] text-center pt-2">
        Stand: 2026. Angaben ohne Gewähr — vor Reisen die offizielle Behörden-Seite
        des Ziellandes prüfen.
      </p>
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-app-text-tertiary)]">
          {label}
        </div>
        <div className="text-sm text-[var(--color-app-text)] leading-snug">{value}</div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-xs font-extrabold uppercase tracking-wide text-[var(--color-app-text-secondary)] mb-2">
        {title}
      </h2>
      <div className="card">{children}</div>
    </div>
  );
}

export function CountryDetail() {
  const { iso } = useParams<{ iso: string }>();
  const country: CountryInfoType | undefined = getCountryInfo(iso ?? "");

  if (!country) {
    return (
      <div className="card text-[var(--color-app-text-secondary)]">
        Land nicht gefunden.{" "}
        <Link to="/laender" className="text-brand-700 hover:underline">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Link to="/laender" className="text-sm text-brand-700 hover:underline">
        ‹ Alle Länder
      </Link>

      <div className="card">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-100 text-brand-700 font-extrabold tracking-wide">
            {country.iso}
          </span>
          <h1 className="text-xl font-bold text-[var(--color-app-text)]">{country.name}</h1>
        </div>
        <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed mt-3">
          {country.summary}
        </p>
      </div>

      <Section title="Rules & Regulations">
        <DataRow label="Mindestalter" value={country.minAge} />
        <DataRow label="Versicherung" value={country.insurance} />
        <DataRow label="Nachtflug" value={country.nightFlight} />
        <DataRow label="Max. Flughöhe" value={country.maxAltitudeM} />
        <DataRow label="Registrierung" value={country.registration} />
      </Section>

      <Section title="Besonderheiten">
        <ul className="space-y-2">
          {country.specialNotes.map((n, i) => (
            <li key={i} className="flex gap-2 text-sm text-[var(--color-app-text)]">
              <span className="text-brand-600 font-bold">•</span>
              <span className="flex-1 leading-snug">{n}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Vor dem Start">
        <ul className="space-y-2">
          {country.checklist.map((n, i) => (
            <li key={i} className="flex gap-2 text-sm text-[var(--color-app-text)]">
              <span className="text-brand-600 font-bold">✓</span>
              <span className="flex-1 leading-snug">{n}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Offizielle Karte / App">
        <a
          href={country.noFlyApp.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-sm font-semibold text-brand-700 hover:underline"
        >
          {country.noFlyApp.name}
        </a>
      </Section>

      <Section title="Behörde">
        {country.authority.url ? (
          <a
            href={country.authority.url}
            target="_blank"
            rel="noreferrer noopener"
            className="block text-sm font-semibold text-brand-700 hover:underline"
          >
            {country.authority.name}
          </a>
        ) : (
          <div className="text-sm text-[var(--color-app-text)]">{country.authority.name}</div>
        )}
        {country.authority.email ? (
          <a
            href={`mailto:${country.authority.email}`}
            className="block text-sm font-semibold text-brand-700 hover:underline mt-1"
          >
            {country.authority.email}
          </a>
        ) : null}
      </Section>

      <p className="text-[11px] italic text-[var(--color-app-text-tertiary)] text-center">
        Stand: 2026. Angaben ohne Gewähr.
      </p>
    </div>
  );
}
